/**
 * Review priority — single score to order cards for Smart Review.
 * Higher = show first.
 */

import type { UserCardProgress } from "./types";
import { REVIEW_PRIORITY } from "./constants";
import { computeForgettingRisk } from "./memory";
import { computeOverdueScore } from "./memory";
import { computeDifficultyScore } from "./difficulty";

/**
 * Compute review priority score (higher = more urgent).
 * Combines: forgetting risk, difficulty, overdue, recency (days since seen).
 */
export function computeReviewPriority(progress: UserCardProgress, now: Date): number {
  const forgettingRisk = progress.forgettingRisk ?? computeForgettingRisk(progress, now);
  const difficultyScore = progress.difficultyScore ?? computeDifficultyScore(progress);
  const overdueScore = progress.overdueScore ?? computeOverdueScore(progress, now);
  const lastSeen = progress.lastSeenAt;
  const daysSinceSeen = lastSeen
    ? (now.getTime() - new Date(lastSeen).getTime()) / (24 * 60 * 60 * 1000)
    : 999;
  const recencyScore = Math.min(100, daysSinceSeen * 5); // not seen for a while = higher

  const priority =
    REVIEW_PRIORITY.WEIGHT_FORGETTING_RISK * forgettingRisk +
    REVIEW_PRIORITY.WEIGHT_DIFFICULTY * difficultyScore +
    REVIEW_PRIORITY.WEIGHT_OVERDUE * Math.min(100, overdueScore * 10) +
    REVIEW_PRIORITY.WEIGHT_RECENCY * recencyScore;

  return Math.round(Math.max(0, Math.min(200, priority)));
}
